import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/* =========================================================================
   ANALYTICS

   First-party. Nothing is sent to a third party, no cookie is set, and the
   only identifiers are two random ids in storage that mean nothing outside
   this site.

     visitor  localStorage    persists, so returning visits are recognisable
     session  sessionStorage  one tab visit, expires after 30 idle minutes

   Time on page is measured on the way out and sent with sendBeacon, which is
   the only transport a browser guarantees to deliver while the page is being
   torn down. A normal fetch is cancelled mid-flight and the reading is lost.
   ========================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'
const SESSION_TTL_MS = 30 * 60 * 1000

const uuid = () =>
  (crypto.randomUUID?.() ??
    'xxxxxxxx-xxxx-4xxx-axxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    }))

function ids() {
  let visitor = ''
  let session = ''
  try {
    visitor = localStorage.getItem('sx_visitor') || ''
    if (!visitor) {
      visitor = uuid()
      localStorage.setItem('sx_visitor', visitor)
    }
    const raw = sessionStorage.getItem('sx_session')
    const last = Number(sessionStorage.getItem('sx_session_at') || 0)
    if (raw && Date.now() - last < SESSION_TTL_MS) {
      session = raw
    } else {
      session = uuid()
      sessionStorage.setItem('sx_session', session)
    }
    sessionStorage.setItem('sx_session_at', String(Date.now()))
  } catch {
    /* Private mode, or storage blocked. Fall back to per-load ids rather
       than dropping the visit entirely. */
    visitor = visitor || uuid()
    session = session || uuid()
  }
  return { visitor, session }
}

function send(payload, viaBeacon = false) {
  const { visitor, session } = ids()
  const body = JSON.stringify({ visitor, session, ...payload })
  const url = `${API_BASE}/track.php`
  try {
    if (viaBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      return
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* Never let measurement break the page it is measuring. */
  }
}

/** Exported so a form can record a real conversion, not just a click. */
export function trackConversion(label, detail) {
  send({ type: 'conversion', path: window.location.pathname, label, detail })
}

export default function Analytics() {
  const location = useLocation()
  const enteredAt = useRef(Date.now())
  const maxScroll = useRef(0)
  const lastPath = useRef('')

  // ---- page views, and the leave beacon for the page being left ----
  useEffect(() => {
    const path = location.pathname + location.search

    if (lastPath.current && lastPath.current !== path) {
      send({
        type: 'leave',
        path: lastPath.current,
        seconds: Math.round((Date.now() - enteredAt.current) / 1000),
        scroll: maxScroll.current,
      })
    }

    const params = new URLSearchParams(location.search)
    send({
      type: 'pageview',
      path,
      title: document.title,
      referrer: lastPath.current ? '' : document.referrer,
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      sw: window.screen?.width,
      sh: window.screen?.height,
      lang: navigator.language,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    lastPath.current = path
    enteredAt.current = Date.now()
    maxScroll.current = 0
  }, [location.pathname, location.search])

  // ---- scroll depth ----
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight
      if (h <= 0) return
      const pct = Math.min(100, Math.round((window.scrollY / h) * 100))
      if (pct > maxScroll.current) maxScroll.current = pct
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ---- clicks on anything that behaves like a control ----
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target?.closest?.('a, button, [role="button"]')
      if (!el) return
      const label =
        el.getAttribute('aria-label') ||
        (el.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 80) ||
        el.getAttribute('title') ||
        ''
      if (!label) return
      send({
        type: 'click',
        path: window.location.pathname,
        label,
        detail: el.getAttribute('href') || '',
      })
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  // ---- the final leave, on tab close or backgrounding ----
  useEffect(() => {
    const leave = () => {
      send(
        {
          type: 'leave',
          path: window.location.pathname + window.location.search,
          seconds: Math.round((Date.now() - enteredAt.current) / 1000),
          scroll: maxScroll.current,
        },
        true
      )
    }
    /* visibilitychange is the reliable one: mobile browsers frequently kill a
       backgrounded tab without ever firing pagehide or unload. */
    const onVis = () => { if (document.visibilityState === 'hidden') leave() }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', leave)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pagehide', leave)
    }
  }, [])

  return null
}
